import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft, Gavel, Share2, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { fmtLongDate } from "../lib/dates";
import { getUserId } from "../lib/user";

/**
 * Board Book — a full-page, print-optimized rendering of a quarterly review's
 * minutes. Designed to be exported as a PDF via the browser's native "Save
 * as PDF" print flow. Zero server-side PDF deps.
 *
 * Print CSS is baked into a <style> tag at the top of the page: it hides the
 * app chrome (nav/footer), swaps to serif body copy, black on white, and
 * enforces page breaks between sections so the artefact reads like a real
 * board book, not a screenshot.
 */
export default function BoardBook() {
  const { id } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chair, setChair] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [mRes, meRes] = await Promise.all([
          api.get(`/meetings/${id}`),
          api.get("/auth/me").catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setMeeting(mRes.data);
        setChair(meRes.data);
      } catch (e) {
        console.debug("board book load failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return <div className="min-h-[40vh] flex items-center justify-center text-[#666]">Loading Board Book…</div>;
  }
  if (!meeting) {
    return <div className="min-h-[40vh] flex items-center justify-center text-[#666]">Not found.</div>;
  }
  if (meeting.cadence !== "quarterly") {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center text-[#bbb]">
        Board Book is only available for quarterly reviews.{" "}
        <Link to={`/meetings/${meeting.id}/minutes`} className="text-[#c89f45] hover:underline">
          Back to minutes
        </Link>
      </div>
    );
  }
  if (!meeting.minutes) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center text-[#bbb]">
        Minutes haven't been generated yet.{" "}
        <Link to={`/meetings/${meeting.id}/minutes`} className="text-[#c89f45] hover:underline">
          Back to minutes
        </Link>
      </div>
    );
  }

  const m = meeting.minutes;
  const isOwner = meeting.owner_id === getUserId();
  const dateStr = fmtLongDate(meeting.completed_at || meeting.created_at);
  const chairName = chair?.name || chair?.email || "The Chair";

  return (
    <>
      {/* Print styles — the whole point of this page. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 18mm 16mm; }
          html, body { background: #ffffff !important; color: #111 !important; }
          .no-print, header[data-testid='app-header'], footer, nav { display: none !important; }
          .board-book-page { color: #111 !important; background: #ffffff !important; box-shadow: none !important; }
          .board-book-page * { color: inherit !important; }
          .board-book-page .accent { color: #8a6c1f !important; }
          .board-book-page .rule { border-color: #ccc !important; }
          .board-book-page .surface { background: #fff !important; border: 1px solid #ddd !important; }
          .board-book-section { break-inside: avoid; page-break-inside: avoid; }
          .board-book-cover { break-after: page; page-break-after: always; }
        }
        .board-book-page {
          font-family: 'Fraunces', 'Cormorant Garamond', 'Georgia', serif;
        }
      `}</style>

      {/* Screen-only control bar */}
      <div className="no-print flex items-center justify-between mb-8 gap-3">
        <Link
          to={`/meetings/${meeting.id}/minutes`}
          className="text-xs text-[#666] hover:text-[#c89f45] flex items-center gap-1 shrink-0"
          data-testid="board-book-back"
        >
          <ArrowLeft size={12} /> Back to minutes
        </Link>
        <div className="flex items-center gap-2">
          {isOwner && (
            <ShareBoardBookButton meetingId={meeting.id} />
          )}
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] font-medium rounded-md px-4 py-2 text-sm transition-colors"
            data-testid="board-book-print"
          >
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>
      </div>

      <article className="board-book-page max-w-3xl mx-auto bg-white text-[#111] rounded-md shadow-2xl p-10 sm:p-14 print:p-0 print:shadow-none">
        {/* Cover */}
        <section className="board-book-cover text-center py-10">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-sm border border-[#c89f45] mb-6">
            <Gavel size={22} className="accent" style={{ color: "#8a6c1f" }} />
          </div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] accent" style={{ color: "#8a6c1f" }}>
            Board Book · Quarterly Review
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-6 leading-tight">{meeting.title}</h1>
          <p className="mt-4 text-sm font-mono text-[#555]">
            {dateStr}
            {meeting.duration_seconds ? ` · ${Math.round(meeting.duration_seconds / 60)} min on the record` : ""}
          </p>
          <div className="mt-14 flex justify-center">
            <div className="w-40 h-px bg-[#c89f45] opacity-60" />
          </div>
          <p className="mt-6 text-xs font-mono uppercase tracking-widest text-[#666]">
            Chairperson · {chairName}
          </p>
        </section>

        {/* Executive Summary */}
        {m.executive_summary && (
          <BookSection eyebrow="§ 1 · Executive Summary" title="The state of things">
            <p className="text-lg leading-relaxed text-[#222]">{m.executive_summary}</p>
          </BookSection>
        )}

        {/* Wins, Lessons, Goals */}
        <BookList eyebrow="§ 2 · Wins" title="What worked" items={m.wins} empty="No wins recorded." />
        <BookList eyebrow="§ 3 · Losses & Lessons" title="What was learned" items={m.lessons} empty="No lessons recorded." />
        <BookList eyebrow="§ 4 · Goals" title="Progress on quarter goals" items={m.goals_progress} empty="No goals reviewed." />

        {/* Financial Snapshot */}
        {m.financial_snapshot && (
          <BookSection eyebrow="§ 5 · Financial Review" title="The money picture">
            <p className="text-base leading-relaxed">{m.financial_snapshot}</p>
          </BookSection>
        )}

        {/* Metric Trends */}
        {Array.isArray(m.metric_trends) && m.metric_trends.length > 0 && (
          <BookList eyebrow="§ 6 · Metric Trends" title="What the numbers said" items={m.metric_trends} />
        )}

        {/* Habit Scorecard */}
        {Array.isArray(m.habit_scorecard) && m.habit_scorecard.length > 0 && (
          <BookList eyebrow="§ 7 · Habit Scorecard" title="90-day habit audit" items={m.habit_scorecard} />
        )}

        {/* Category Reflection (5 Pillars) — quarterly signature section */}
        {m.category_reflection && typeof m.category_reflection === "object" && (
          <BookSection eyebrow="§ 8 · Category Reflection" title="The 5 pillars">
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              {["health", "business", "relationships", "money", "craft"].map((k) => {
                const val = (m.category_reflection[k] || "").trim();
                if (!val) return null;
                return (
                  <div
                    key={k}
                    className="rounded-md p-4 border border-[#ddd] bg-[#faf7f0]"
                    data-testid={`board-book-pillar-${k}`}
                  >
                    <p className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8a6c1f" }}>
                      {k}
                    </p>
                    <p className="text-sm mt-2 leading-relaxed text-[#222]">{val}</p>
                  </div>
                );
              })}
            </div>
          </BookSection>
        )}

        {/* Strategic Shifts */}
        {Array.isArray(m.strategic_shifts) && m.strategic_shifts.length > 0 && (
          <BookList eyebrow="§ 9 · Strategic Shifts" title="Pivots, doubles-downs, kills" items={m.strategic_shifts} />
        )}

        {/* Vision Alignment */}
        {m.vision_alignment && (
          <BookSection eyebrow="§ 10 · Vision Alignment" title="North Star check">
            <p className="text-base leading-relaxed">{m.vision_alignment}</p>
          </BookSection>
        )}

        {/* Quarter Pattern Analysis */}
        {m.quarter_pattern_analysis && (
          <BookSection eyebrow="§ 11 · Pattern Analysis" title="What the three monthlies revealed">
            <p className="text-base leading-relaxed">{m.quarter_pattern_analysis}</p>
          </BookSection>
        )}

        {/* Next Quarter's Bets */}
        {Array.isArray(m.next_quarter_bets) && m.next_quarter_bets.length > 0 && (
          <BookSection eyebrow="§ 12 · Next Quarter's Bets" title="Where the chips are going">
            <ol className="mt-2 space-y-4">
              {m.next_quarter_bets.map((bet, i) => (
                <li key={i} className="flex gap-4">
                  <span
                    className="font-serif text-xl leading-none shrink-0"
                    style={{ color: "#8a6c1f" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <p className="font-serif text-lg">{bet.title}</p>
                    {bet.description && (
                      <p className="text-sm mt-1.5 leading-relaxed">{bet.description}</p>
                    )}
                    {bet.why_it_matters && (
                      <p className="text-xs mt-2 leading-relaxed italic text-[#555]">
                        Why it matters — {bet.why_it_matters}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </BookSection>
        )}

        {/* Action Items */}
        {Array.isArray(m.action_items) && m.action_items.length > 0 && (
          <BookSection eyebrow="§ 13 · Action Items" title="Commitments on the record">
            <ol className="mt-2 space-y-2 list-decimal list-inside">
              {m.action_items.map((a, i) => (
                <li key={i} className="text-base leading-relaxed">
                  <span className="font-medium">{a.title}</span>
                  {a.description ? <span className="text-[#555]"> — {a.description}</span> : null}
                </li>
              ))}
            </ol>
          </BookSection>
        )}

        {/* Next Focus */}
        {m.next_focus && (
          <BookSection eyebrow="§ 14 · Next Focus" title="The one thing next quarter must nail">
            <p className="font-serif text-xl leading-snug">{m.next_focus}</p>
          </BookSection>
        )}

        {/* Signature block */}
        <section className="board-book-section mt-16">
          <div className="rule border-t border-[#ccc] pt-10">
            <div className="grid sm:grid-cols-2 gap-10">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#555]">
                  Signed · Chairperson
                </p>
                <div className="mt-6 h-10 border-b border-[#333]" />
                <p className="mt-3 font-serif text-base">{chairName}</p>
                <p className="text-xs text-[#555]">{isOwner ? "Owner" : "Participant"}</p>
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#555]">
                  Date
                </p>
                <div className="mt-6 h-10 border-b border-[#333]" />
                <p className="mt-3 font-serif text-base">{dateStr}</p>
                <p className="text-xs text-[#555]">Session of record</p>
              </div>
            </div>
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-center text-[#888] mt-12">
            Boardroom · foundtheroom.com · Session integrity is everything
          </p>
        </section>
      </article>
    </>
  );
}


function BookSection({ eyebrow, title, children }) {
  return (
    <section className="board-book-section mt-12">
      <p className="text-[10px] font-mono uppercase tracking-widest accent" style={{ color: "#8a6c1f" }}>
        {eyebrow}
      </p>
      <h2 className="font-serif text-2xl sm:text-3xl mt-3 leading-snug">{title}</h2>
      <div className="mt-5">{children}</div>
    </section>
  );
}


function BookList({ eyebrow, title, items, empty }) {
  if (!Array.isArray(items) || items.length === 0) {
    if (!empty) return null;
    return (
      <BookSection eyebrow={eyebrow} title={title}>
        <p className="text-sm text-[#555] italic">{empty}</p>
      </BookSection>
    );
  }
  return (
    <BookSection eyebrow={eyebrow} title={title}>
      <ul className="mt-2 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-base leading-relaxed flex gap-3">
            <span className="accent shrink-0" style={{ color: "#8a6c1f" }}>·</span>
            <span>{typeof it === "string" ? it : JSON.stringify(it)}</span>
          </li>
        ))}
      </ul>
    </BookSection>
  );
}


/**
 * Owner-only "Share" affordance. First click mints (or reuses) a 30-day
 * signed URL and immediately copies it to the clipboard. A follow-up click
 * exposes the copy/revoke actions in a small popover so the user can rotate
 * the link if they change their mind.
 */
function ShareBoardBookButton({ meetingId }) {
  const [share, setShare] = useState(null); // { token, expires_at, url, views }
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const buildUrl = (token) => `${window.location.origin}/board-book/${token}`;

  const mintAndCopy = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/meetings/${meetingId}/board-book/share`, {});
      const url = buildUrl(r.data.token);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch {}
      setShare({ ...r.data, url });
      setOpen(true);
      toast.success(r.data.reused ? "Link copied · reusing existing share" : "Share link copied");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Could not create share link.");
    } finally {
      setBusy(false);
    }
  };

  const copyAgain = async () => {
    if (!share?.url) return;
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Link copied");
    } catch {}
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this Board Book share? Anyone with the link will get 'Link unavailable'.")) return;
    setBusy(true);
    try {
      await api.delete(`/meetings/${meetingId}/board-book/share`);
      setShare(null);
      setOpen(false);
      toast.success("Share link revoked");
    } catch (e) {
      toast.error("Could not revoke share link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={share ? () => setOpen((o) => !o) : mintAndCopy}
        disabled={busy}
        className="inline-flex items-center gap-2 border border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45] hover:text-[#0a0a0a] font-medium rounded-md px-4 py-2 text-sm transition-colors disabled:opacity-40"
        data-testid="board-book-share-btn"
      >
        <Share2 size={14} /> {busy && !share ? "Creating…" : "Share"}
      </button>
      {open && share && (
        <div
          className="absolute right-0 top-full mt-2 w-80 bg-[#0a0a0a] border border-[#2a2a2a] rounded-md p-4 shadow-2xl z-30"
          data-testid="board-book-share-popover"
        >
          <p className="label-eyebrow text-[#c89f45]">Read-only link</p>
          <p className="text-[10px] font-mono text-[#666] mt-1">
            Valid until {new Date(share.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })} · {share.views || 0} view{(share.views || 0) === 1 ? "" : "s"}
          </p>
          <div className="mt-3 flex items-center gap-2 bg-[#121212] border border-[#2a2a2a] rounded-md p-2">
            <span className="text-[11px] font-mono text-[#ddd] truncate flex-1" data-testid="board-book-share-url">{share.url}</span>
            <button
              type="button"
              onClick={copyAgain}
              className="text-[#c89f45] hover:text-[#eaeaea] shrink-0"
              aria-label="Copy link"
              data-testid="board-book-share-copy"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
          <button
            type="button"
            onClick={revoke}
            disabled={busy}
            className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#888] hover:text-red-400 transition-colors disabled:opacity-40"
            data-testid="board-book-share-revoke"
          >
            Revoke link
          </button>
        </div>
      )}
    </div>
  );
}
